export const RECOMMENDATION_DRAFT_SCHEMA_VERSION = 1 as const;
export const RECOMMENDATION_DRAFT_TTL_MS = 3 * 24 * 60 * 60 * 1000;

export const recommendationPurposes = ["自己使用", "送礼", "约会", "通勤", "其他场景"] as const;
export type RecommendationPurpose = (typeof recommendationPurposes)[number];
export type RecommendationMode = "guided" | "smart";

export const recommendationFieldKeys: Record<RecommendationPurpose, readonly string[]> = {
  "自己使用": ["gender", "age", "job", "style"],
  "送礼": ["recipient", "age", "relation", "occasion"],
  "约会": ["dateTarget", "mood", "time", "place"],
  "通勤": ["industry", "space", "dress", "presence"],
  "其他场景": ["scene", "forWhom", "season", "wish"],
};

export type RecommendationContext = {
  purpose: string;
  details: string[];
  answers?: Record<string, string>;
  scent: string;
  budget: string;
  brand: string;
  description?: string;
};

export type RecommendationPreferences = {
  scents: string[];
  budget: string;
  brand: string;
};

export type RecommendationDraft = {
  schemaVersion: typeof RECOMMENDATION_DRAFT_SCHEMA_VERSION;
  updatedAt: number;
  expiresAt: number;
  mode: RecommendationMode;
  activePurpose: RecommendationPurpose | "";
  freeText: string;
  scenarioAnswers: Record<RecommendationPurpose, Record<string, string>>;
  preferences: RecommendationPreferences;
};

function emptyScenarioAnswers(): RecommendationDraft["scenarioAnswers"] {
  return {
    "自己使用": {},
    "送礼": {},
    "约会": {},
    "通勤": {},
    "其他场景": {},
  };
}
export function createRecommendationDraft(now = Date.now()): RecommendationDraft {
  return {
    schemaVersion: RECOMMENDATION_DRAFT_SCHEMA_VERSION,
    updatedAt: now,
    expiresAt: now + RECOMMENDATION_DRAFT_TTL_MS,
    mode: "guided",
    activePurpose: "",
    freeText: "",
    scenarioAnswers: emptyScenarioAnswers(),
    preferences: {
      scents: ["不限香调"],
      budget: "不限预算",
      brand: "不限品牌",
    },
  };
}

export function isRecommendationPurpose(value: unknown): value is RecommendationPurpose {
  return typeof value === "string" && recommendationPurposes.includes(value as RecommendationPurpose);
}

function cleanAnswers(value: unknown, purpose: RecommendationPurpose): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const source = value as Record<string, unknown>;
  return Object.fromEntries(
    recommendationFieldKeys[purpose]
      .map((key) => [key, typeof source[key] === "string" ? source[key] : ""] as const)
      .filter(([, answer]) => answer !== ""),
  );
}

export function normalizeRecommendationDraft(value: unknown, now = Date.now()): RecommendationDraft | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const source = value as Record<string, unknown>;
  if (source.schemaVersion !== RECOMMENDATION_DRAFT_SCHEMA_VERSION) return null;
  if (typeof source.expiresAt !== "number" || source.expiresAt <= now) return null;

  const rawScenarios = source.scenarioAnswers && typeof source.scenarioAnswers === "object"
    ? source.scenarioAnswers as Record<string, unknown>
    : {};
  const rawPreferences = source.preferences && typeof source.preferences === "object"
    ? source.preferences as Record<string, unknown>
    : {};
  const scents = Array.isArray(rawPreferences.scents)
    ? [...new Set(rawPreferences.scents.filter((item): item is string => typeof item === "string" && Boolean(item)))]
    : [];

  return {
    schemaVersion: RECOMMENDATION_DRAFT_SCHEMA_VERSION,
    updatedAt: typeof source.updatedAt === "number" ? source.updatedAt : now,
    expiresAt: source.expiresAt,
    mode: source.mode === "smart" ? "smart" : "guided",
    activePurpose: isRecommendationPurpose(source.activePurpose) ? source.activePurpose : "",
    freeText: typeof source.freeText === "string" ? source.freeText : "",
    scenarioAnswers: {
      "自己使用": cleanAnswers(rawScenarios["自己使用"], "自己使用"),
      "送礼": cleanAnswers(rawScenarios["送礼"], "送礼"),
      "约会": cleanAnswers(rawScenarios["约会"], "约会"),
      "通勤": cleanAnswers(rawScenarios["通勤"], "通勤"),
      "其他场景": cleanAnswers(rawScenarios["其他场景"], "其他场景"),
    },
    preferences: {
      scents: scents.length ? scents : ["不限香调"],
      budget: typeof rawPreferences.budget === "string" && rawPreferences.budget ? rawPreferences.budget : "不限预算",
      brand: typeof rawPreferences.brand === "string" && rawPreferences.brand ? rawPreferences.brand : "不限品牌",
    },
  };
}
